import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Bell,
  User as UserIcon,
  Settings,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";
import type { User } from "../types/Sidebar.type";
import { CLINICAL_FONT_FAMILY } from "../components/shared/constants";
import { normalizeRole, ROLE_DISPLAY_CONFIG } from "@/types/role.type";
import { useHealthCheck } from "../hooks/useHealthCheck";
import { currentConfig } from "@/config/environment";
import { getToken } from "@/utils/auth";
import { clearServiceWorkerAndCacheStorage } from "@/utils/pwa";
import type { Study } from "@/pages/StudyManagement";
import StudyContextSelector from "@/pages/UserRoles/StudyContextSelector";
import StudyContextCoachmark from "@/pages/UserRoles/StudyContextCoachmark";
import { isUserPmOrCta, NO_STUDY_SENTINEL } from "@/utils/studyContext";

const COACHMARK_STORAGE_PREFIX = "studyContextCoachmarkSeen:";
const COACHMARK_DELAY_MS = 900;

// Helper functions to extract user information
const getUserName = (user: User): string => {
  return user.username?.split("@")[0] ?? "User";
};

const getUserRole = (user: User): string => {
  return user.roles && user.roles.length > 0 && user.roles[0]
    ? user.roles[0].role
    : "Tenant";
};

interface HeaderProps {
  onMenuClick: () => void;
  user: User;
  onLogout: () => void;
  availableStudies?: Study[];
  selectedStudyId?: string;
  userStudyRoles?: Record<string, string>;
  onStudyChange?: (studyId: string) => void;
}

export const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  user,
  onLogout,
  availableStudies = [],
  selectedStudyId,
  userStudyRoles = {},
  onStudyChange,
}) => {
  const navigate = useNavigate();
  const [profileMenuAnchor, setProfileMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  const [tenantLogoUrl, setTenantLogoUrl] = React.useState<string | null>(null);
  const [showFront, setShowFront] = React.useState(true);

  const studySelectorRef = React.useRef<HTMLDivElement | null>(null);
  const [coachmarkOpen, setCoachmarkOpen] = React.useState(false);

  const showStudySelector = !user.isAdmin;
  const hasStudySelected =
    !!selectedStudyId && selectedStudyId !== NO_STUDY_SENTINEL;
  const coachmarkKey = `${COACHMARK_STORAGE_PREFIX}${user.id}`;

  // First-run coachmark: point the user at the study selector once per account
  useEffect(() => {
    if (!showStudySelector || !user.id) {
      return;
    }
    if (localStorage.getItem(coachmarkKey) === "true") {
      return;
    }
    const timer = setTimeout(() => setCoachmarkOpen(true), COACHMARK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [showStudySelector, user.id, coachmarkKey]);

  const dismissCoachmark = React.useCallback(() => {
    setCoachmarkOpen(false);
    localStorage.setItem(coachmarkKey, "true");
  }, [coachmarkKey]);

  const handleStudyChange = React.useCallback(
    (studyId: string) => {
      if (coachmarkOpen) {
        dismissCoachmark();
      }
      onStudyChange?.(studyId);
    },
    [coachmarkOpen, dismissCoachmark, onStudyChange],
  );

  // Fetch tenant logo
  useEffect(() => {
    if (!user.tenantId || !currentConfig.apiBaseUrl) {
      return;
    }
    const token = getToken();
    fetch(`${currentConfig.apiBaseUrl}/tenants/${user.tenantId}/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("logo fetch failed");
        }
        return res.json() as Promise<{
          statusCode: number;
          message: string;
          data: { logoUrl: string | null };
        }>;
      })
      .then(({ data }) => {
        if (data?.logoUrl) {
          setTenantLogoUrl(data.logoUrl);
        }
      })
      .catch(() => {
        /* fall back to app icon */
      });
  }, [user.tenantId]);

  // Flip every 5 s when a tenant logo is available
  useEffect(() => {
    if (!tenantLogoUrl) {
      return;
    }
    const id = setInterval(() => setShowFront((f) => !f), 5000);
    return () => clearInterval(id);
  }, [tenantLogoUrl]);

  const { overallStatus, isLoading: healthLoading } = useHealthCheck({
    checkOnMount: true,
    autoRefresh: true,
    refreshInterval: 60000,
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [rocketPhase, setRocketPhase] = React.useState<
    "idle" | "launch" | "done"
  >("idle");

  const handleHardRefresh = async () => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    setRocketPhase("launch");
    // Let the rocket animation play for 1.8s, then do the actual work
    await new Promise((res) => setTimeout(res, 1800));
    try {
      await clearServiceWorkerAndCacheStorage();
    } finally {
      setRocketPhase("done");
      await new Promise((res) => setTimeout(res, 400));
      window.location.reload();
    }
  };

  const isHealthy = overallStatus === "healthy";
  const healthColor = healthLoading
    ? "#94A3B8"
    : isHealthy
      ? "#22C55E"
      : "#EF4444";
  const healthLabel = healthLoading
    ? "Checking system health…"
    : isHealthy
      ? "All systems operational"
      : "Service disruption detected";

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileMenuAnchor(null);
  };

  const getRoleColor = (role: string) =>
    ROLE_DISPLAY_CONFIG[normalizeRole(role)].color;

  const getInitials = (name: string) => {
    if (!name) {
      return "U";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid #E2E8F0",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        color: "#0F172A",
      }}
    >
      <Toolbar sx={{ fontFamily: CLINICAL_FONT_FAMILY, position: "relative" }}>
        {/* ── Left section ── */}
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={onMenuClick}
            edge="start"
            sx={{
              mr: 2,
              color: "#64748B",
              "&:hover": {
                backgroundColor: "#F1F5F9",
              },
            }}
          >
            <MenuIcon size={20} />
          </IconButton>

          {/* Logo and Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Flip card — front: app icon, back: tenant logo */}
            <Box
              sx={{
                width: !showFront && tenantLogoUrl ? 120 : 32,
                height: !showFront && tenantLogoUrl ? 40 : 32,
                perspective: "600px",
                flexShrink: 0,
                transition: "width 0.5s ease",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  transformStyle: "preserve-3d",
                  transition: "transform 0.6s ease",
                  transform: showFront ? "rotateY(0deg)" : "rotateY(180deg)",
                }}
              >
                {/* Front face — AIClinTrack app icon */}
                <Box
                  component="img"
                  src="/appIcon192.png"
                  alt="AIClinTrack"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "8px",
                    objectFit: "contain",
                    backfaceVisibility: "hidden",
                  }}
                />
                {/* Back face — tenant logo */}
                {tenantLogoUrl && (
                  <Box
                    component="img"
                    src={tenantLogoUrl}
                    alt="Tenant Logo"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                      objectFit: "contain",
                      backfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  />
                )}
              </Box>
            </Box>
            {(showFront || !tenantLogoUrl) && (
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 600,
                  color: "#0F172A",
                  letterSpacing: "-0.025em",
                  transition: "opacity 0.3s ease",
                }}
              >
                AIClinTrack
              </Typography>
            )}
          </Box>
        </Box>
        {/* ── Center section — absolutely centered with fixed width ── */}
        {showStudySelector && (
          <Box
            ref={studySelectorRef}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
              width: { sm: 200, md: 260, lg: 360 },
              maxWidth: "calc(100% - 360px)",
              overflow: "hidden",
            }}
          >
            {availableStudies.length > 0 && onStudyChange ? (
              <StudyContextSelector
                studies={availableStudies}
                selectedStudyId={selectedStudyId}
                userStudyRoles={userStudyRoles}
                onStudyChange={handleStudyChange}
                showNoStudyOption={isUserPmOrCta(user)}
                needsAttention={!hasStudySelected || coachmarkOpen}
              />
            ) : isUserPmOrCta(user) && onStudyChange ? (
              // PM / CTA with no studies yet — show selector with "No Study Selected"
              <StudyContextSelector
                studies={[]}
                selectedStudyId={selectedStudyId}
                userStudyRoles={userStudyRoles}
                onStudyChange={handleStudyChange}
                showNoStudyOption
                needsAttention={!hasStudySelected || coachmarkOpen}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontSize: "0.75rem",
                }}
              >
                {getUserRole(user)
                  .replace(/([A-Z])/g, " $1")
                  .trim() || "User"}
              </Typography>
            )}
          </Box>
        )}
        {/* ── Right section ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          {/* Hard Refresh Button */}
          <Tooltip title="Check for updates" arrow>
            <IconButton
              size="medium"
              onClick={() => {
                void handleHardRefresh();
              }}
              disabled={isRefreshing}
              sx={{
                color: "#64748B",
                mr: 0.5,
                transition: "color 0.2s",
                "&:hover": { color: "#0B5FFF", backgroundColor: "#EFF6FF" },
              }}
            >
              <RefreshCw size={18} />
            </IconButton>
          </Tooltip>

          {/* Rocket Refresh Overlay — rendered via portal so it covers the full page */}
          {isRefreshing &&
            createPortal(
              <Box
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 99999,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
                  animation: "fadeIn 0.3s ease",
                  "@keyframes fadeIn": {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                {/* Stars */}
                {[...Array(18)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      width: i % 3 === 0 ? 4 : 2,
                      height: i % 3 === 0 ? 4 : 2,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.7)",
                      top: `${Math.sin(i * 137.5) * 40 + 50}%`,
                      left: `${Math.cos(i * 137.5) * 45 + 50}%`,
                      animation: `twinkle ${1 + (i % 3) * 0.4}s ease-in-out infinite alternate`,
                      "@keyframes twinkle": {
                        from: { opacity: 0.2 },
                        to: { opacity: 1 },
                      },
                    }}
                  />
                ))}

                {/* Rocket */}
                <Box
                  sx={{
                    fontSize: rocketPhase === "launch" ? "5rem" : "4rem",
                    lineHeight: 1,
                    animation:
                      rocketPhase === "launch"
                        ? "rocketLaunch 1.8s ease-in forwards"
                        : "none",
                    "@keyframes rocketLaunch": {
                      "0%": {
                        transform: "translateY(0) rotate(-45deg)",
                        opacity: 1,
                      },
                      "60%": {
                        transform: "translateY(-60px) rotate(-45deg)",
                        opacity: 1,
                      },
                      "100%": {
                        transform: "translateY(-200px) rotate(-45deg)",
                        opacity: 0,
                      },
                    },
                  }}
                >
                  🚀
                </Box>

                {/* Flame / exhaust */}
                {rocketPhase === "launch" && (
                  <Box
                    sx={{
                      fontSize: "1.8rem",
                      mt: "-0.5rem",
                      animation: "flicker 0.15s linear infinite alternate",
                      "@keyframes flicker": {
                        from: {
                          transform: "scaleY(0.8) scaleX(0.9)",
                          opacity: 0.8,
                        },
                        to: {
                          transform: "scaleY(1.2) scaleX(1.1)",
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    🔥
                  </Box>
                )}

                <Typography
                  sx={{
                    mt: 4,
                    color: "white",
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: "1.35rem",
                    letterSpacing: "0.02em",
                    textAlign: "center",
                    animation: "pulse 1s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.55 },
                    },
                  }}
                >
                  {rocketPhase === "done"
                    ? "All done! Reloading the app…"
                    : "Updating AIClinTrack…"}
                </Typography>

                <Typography
                  sx={{
                    mt: 1.5,
                    color: "rgba(255,255,255,0.85)",
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontSize: "0.95rem",
                    textAlign: "center",
                    maxWidth: 320,
                  }}
                >
                  {rocketPhase === "done"
                    ? "Cache cleared successfully. You’ll see the latest version in just a moment."
                    : "Clearing cached data & refreshing service workers to load the latest version."}
                </Typography>

                {/* Step indicators */}
                <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                  {[
                    { label: "Clearing cache", done: rocketPhase === "done" },
                    { label: "Reloading", done: false },
                  ].map((step, i) => (
                    <Box
                      key={i}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: step.done
                            ? "#fff"
                            : rocketPhase === "launch" && i === 0
                              ? "rgba(255,255,255,0.9)"
                              : "rgba(255,255,255,0.35)",
                          transition: "background-color 0.4s",
                        }}
                      />
                      <Typography
                        sx={{
                          color: step.done ? "white" : "rgba(255,255,255,0.5)",
                          fontFamily: CLINICAL_FONT_FAMILY,
                          fontSize: "0.72rem",
                          transition: "color 0.4s",
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>,
              document.body,
            )}

          {/* Notification Bell — Coming Soon */}
          <Tooltip title="Notifications (Coming Soon)" arrow>
            <span>
              <IconButton
                size="medium"
                disabled
                sx={{
                  color: "#CBD5E1",
                  mr: 1,
                }}
              >
                <Bell size={20} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Health Status Indicator */}
          <Tooltip title={healthLabel} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
                p: "8px",
                color: healthColor,
                transition: "color 0.4s ease",
              }}
            >
              {isHealthy || healthLoading ? (
                <ShieldCheck
                  size={20}
                  fill={healthColor}
                  stroke="white"
                  strokeWidth={1.5}
                />
              ) : (
                <ShieldAlert
                  size={20}
                  fill={healthColor}
                  stroke="white"
                  strokeWidth={1.5}
                />
              )}
            </Box>
          </Tooltip>

          {/* User Profile */}
          <Box
            onClick={handleProfileClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              py: 1,
              px: 1.5,
              borderRadius: 2,
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#F1F5F9",
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: `${getRoleColor(getUserRole(user))}20`,
                color: getRoleColor(getUserRole(user)),
                fontSize: "0.875rem",
                fontWeight: 600,
                fontFamily: CLINICAL_FONT_FAMILY,
              }}
            >
              {getInitials(getUserName(user))}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#374151",
                  fontWeight: 500,
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontSize: "0.875rem",
                }}
              >
                {getUserName(user)}
              </Typography>
            </Box>
          </Box>
        </Box>{" "}
        {/* end right section */}
        {/* Profile Menu */}
        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileClose}
          PaperProps={{
            sx: {
              minWidth: 200,
              borderRadius: 2,
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              fontFamily: CLINICAL_FONT_FAMILY,
            },
          }}
          disableScrollLock
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/profile");
            }}
            sx={{
              py: 1.5,
              fontFamily: CLINICAL_FONT_FAMILY,
            }}
          >
            <UserIcon size={16} style={{ marginRight: 12 }} />
            Profile
          </MenuItem>
          {/* <MenuItem
            onClick={handleProfileClose}
            sx={{
              py: 1.5,
              fontFamily: CLINICAL_FONT_FAMILY,
            }}
          >
            <Settings size={16} style={{ marginRight: 12 }} />
            Settings
          </MenuItem> */}
          <MenuItem
            onClick={() => {
              handleProfileClose();
              if (user.isAdmin) {
                navigate("/settings");
              }
            }}
            disabled={!user.isAdmin}
            sx={{
              py: 1.5,
              fontFamily: CLINICAL_FONT_FAMILY,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              opacity: user.isAdmin ? 1 : 0.6,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Settings size={16} style={{ marginRight: 12 }} />
              Settings
            </Box>
            {!user.isAdmin && (
              <Chip
                label="Admin only"
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  borderColor: "#CBD5E1",
                  color: "#64748B",
                }}
              />
            )}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              handleProfileClose();
              onLogout();
            }}
            sx={{
              py: 1.5,
              color: "#EF4444",
              fontFamily: CLINICAL_FONT_FAMILY,
            }}
          >
            <LogOut size={16} style={{ marginRight: 12 }} />
            Logout
          </MenuItem>
        </Menu>
        {showStudySelector && (
          <StudyContextCoachmark
            anchorEl={studySelectorRef.current}
            open={coachmarkOpen}
            onDismiss={dismissCoachmark}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};
